import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Category } from '../../../../core/models/category.model';
import { FamiliaProduto } from '../../../../core/integrations/nosERP/models/category.model';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

export class CategoryMapper {
  /**
   * Maps a FamiliaProduto model to a Category model.
   * @param familiaProduto - The FamiliaProduto model to transform.
   * @returns The transformed Category model.
   */
  static toCategory(familiaProduto: FamiliaProduto): Category {
    return {
      id: familiaProduto.id.toString(),
      name: familiaProduto.nome,
      image: familiaProduto.descricao, // Assuming descricao is used as the image source
      active: true // Set to `true` by default or based on some logic
      //TODO codigo antigo
      // parentCategory: familiaProduto.sub_familia_produtos
      //   ? familiaProduto.sub_familia_produtos.map(subFamilia => this.toCategory(subFamilia))
      //   : undefined
    };
  }

  /**
   * Maps a Category model to a FamiliaProduto model.
   * @param category - The Category model to transform.
   * @param parentId - The parent ID to attach to the FamiliaProduto model. Optional.
   * @returns The transformed FamiliaProduto model.
   */
  static toFamiliaProduto(category: Category, parentId: number | string = 0): FamiliaProduto {
    return {
      id: category.id || 0, // Defaulting to 0 or null if no ID is present
      nome: category.name,
      descricao: category.image || '', // Map `image` back to `descricao`
      id_funcionario: Number(parentId), // Assuming it's the parent category's ID
      sub_familia_produtos: null
      //TODO codigo antigo
      //   category.parentCategory
      // ? category.parentCategory.map(subCategory => this.toFamiliaProduto(subCategory, category.id || 0))
      // : []
    };
  }
}

@Injectable({
  providedIn: 'root'
})
export class CategoriesServiceAdapter {
  private _categories = new BehaviorSubject<Category[]>([]);

  constructor(private _httpClient: HttpClient) {
    // Initialize the BehaviorSubject by fetching categories
    this.fetchCategories().subscribe();
  }

  private flattenToCategoryArray(
    categories: FamiliaProduto[],
    parent: Category | null = null // Parent starts as null for top-level categories
  ): Category[] {
    const result: Category[] = [];

    for (const category of categories) {
      // Create the corresponding flat structure for each category
      const flatCategory: Category = {
        id: category.id.toString(),
        name: category.nome,
        image: category.image,
        active: true, // Assume all categories are active, adjust as needed
        parentCategory: parent
      };

      // Add the current category to the result list
      result.push(flatCategory);

      // Recursively process its children - check if sub_familia_produtos exists and is an array
      if (category.sub_familia_produtos && Array.isArray(category.sub_familia_produtos)) {
        result.push(...this.flattenToCategoryArray(category.sub_familia_produtos, flatCategory));
      }
    }

    return result;
  }

  /**
   * Fetches categories and processes them to include categories.
   * Only called once, during initialization.
   */
  public fetchCategories(): Observable<Category[]> {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user) {
      return of([]);
    }
    return this._httpClient.get<FamiliaProduto[]>('api/categoria/' + user.id_funcionario).pipe(
      map(familiaProdutos => this.flattenToCategoryArray(familiaProdutos)),
      tap(result => {
        this._categories.next(result);
      })
    );
  }

  /**
   * Returns all categories as an observable.
   */
  getCategories(): Observable<Category[]> {
    return this._categories.asObservable();
  }

  /**
   * Clears the categories cache by resetting the BehaviorSubject to an empty array.
   * This should be called when a user logs out.
   */
  clearCategoriesCache(): void {
    this._categories.next([]);
  }
}
